import { HubConnectionState } from "@microsoft/signalr";
import { loadWrapper, useUnsavedChanges } from "aic";
import { PopupAlertModal } from "components/common/modals/PopupAlertModal";
import { HierarchyQueryParams } from "functions/api/hierarchy/HierarchyQueryParams";
import { TocItemsUpdateProps, updateTocItems } from "functions/api/toc/updateTocItems";
import { doReload } from "functions/doReload";
import { isEqual } from "lodash";
import {
  ReactNode,
  useContext,
  useEffect,
  useEffectEvent,
  useReducer,
  useState,
  useTransition,
} from "react";
import { useNavigation } from "react-router-dom";
import { eAccessRights, eHierarchyLevel, eTlfItemType } from "../functions/api/enums";
import { IRequestStatus } from "../functions/api/interface";
import { AppSettingsContext } from "./AppSettingsContext";
import {
  SAVE_HIERARCHY_COMPLETED,
  SET_HIERARCHY_ID,
  UPDATE_HIERARCHY,
} from "./hierarchy/interface";
import { HierarchyContext } from "./HierarchyContext";
import { HubGroupMessage, TocActionSignal } from "./signalr/interface";
import { SignalRContext } from "./SignalRProvider";
import { MosaicTocEntryDto, MosaicTocSectionDto, TocUpdateMessage } from "./toc/interface";
import { processActionFromSignal } from "./toc/processActionFromSignal";
import { refreshToc } from "./toc/refreshToc";
import {
  SAVE_FAIL,
  SAVE_SUCCESS,
  SET_TYPE_FILTER,
  tocContextReducer,
  UPDATE_READONLY,
} from "./toc/tocContextReducer";
import { loadTocTlfItemFilterFromStorage } from "./toc/tocTlfItemFilter";
import { defaultTocState, TocContext } from "./TocContext";

export interface TocContextProviderProps {
  children: ReactNode;
}

/**
 * Provides Table of Contents (TOC) context and management functionality to child components.
 *
 * @param params - The component props
 * @param params.children - The child components to render within the provider
 */
export const TocContextProvider = ({ children }: TocContextProviderProps): React.ReactElement => {
  const appSettings = useContext(AppSettingsContext);
  const hierarchyContext = useContext(HierarchyContext);
  const hierarchy = hierarchyContext?.hierarchy;
  const accessRights = hierarchyContext?.accessRights;
  const hierarchyDispatch = hierarchyContext?.dispatch;
  const { navigate } = useUnsavedChanges("toc-context-provider");
  const signalR = useContext(SignalRContext);
  const [receivedMessages, setReceivedMessages] = useState<TocUpdateMessage[]>([]);
  const [showSaveAlert, setShowSaveAlert] = useState<boolean>(false);
  const [state, dispatch] = useReducer(tocContextReducer, defaultTocState);
  const [tocIdFromMessage, setTocIdFromMessage] = useState<string>();
  const [, startTransition] = useTransition();

  /**
   * Set read only mode
   */
  useEffect(() => {
    if (hierarchy && accessRights) {
      const readOnly =
        !accessRights.includes(eAccessRights.WriteToTlfItemsInHierarchy) ||
        hierarchy.isBackupVersion;
      dispatch({
        operation: UPDATE_READONLY,
        readOnly,
      });
    }
  }, [accessRights, hierarchy]);

  // SignalR Group
  const signalRGroup =
    hierarchyContext && hierarchyContext.hierarchy ? `toc${hierarchyContext.hierarchy.id}` : null;

  /**
   * Load view mode from localStorage on initialization
   */
  useEffect(() => {
    const savedFilter = loadTocTlfItemFilterFromStorage();
    if (!isEqual(savedFilter, state.showTypes)) {
      dispatch({
        operation: SET_TYPE_FILTER,
        showTypes: savedFilter,
      });
    }
  }, [state.showTypes]);

  /**
   * Join signalR group for the hierarchy
   * This is done to receive tocSave and tocUpdates for the ToC
   */
  const [sendGroupRequest, setSendGroupRequest] = useState<string>("❄️");
  useEffect(() => {
    if (
      signalR &&
      signalR.connection?.state === HubConnectionState.Connected &&
      signalRGroup &&
      sendGroupRequest !== signalRGroup
    ) {
      signalR.connectToGroup(signalRGroup);
      startTransition(() => {
        setSendGroupRequest(signalRGroup);
      });
    }
  }, [sendGroupRequest, signalR, signalRGroup, signalR?.connection?.state]);

  const [saveRequestStatus, setSaveRequestStatus] = useState<IRequestStatus<TocItemsUpdateProps>>({
    requesting: false,
    error: false,
  });

  /**
   * Save items provider function
   * @param tocItemsToUpdate - The items to update
   */
  const saveItems = useEffectEvent(async (tocItemsToUpdate: TocItemsUpdateProps) => {
    const changeList = state.tocItems.filter((i) => i.changed).map((i) => i.id);
    const response = await loadWrapper(
      updateTocItems,
      tocItemsToUpdate,
      { ...saveRequestStatus, requestedId: undefined },
      setSaveRequestStatus,
      appSettings.backEndUrl,
    );
    /* istanbul ignore else */
    if (response?.success && hierarchy) {
      const newHierarchy = {
        ...hierarchy,
        tocMajorVersion: response.data.tocMajorVersion,
        tocMinorVersion: response.data.tocMinorVersion,
        sections: response.data.sections,
      };
      hierarchyDispatch?.({
        operation: UPDATE_HIERARCHY,
        hierarchy: newHierarchy,
      });
      dispatch({
        operation: SAVE_SUCCESS,
        itemsUpdate: tocItemsToUpdate,
        saveTimeStamp: Date.now(),
      });
      /* istanbul ignore else */
      if (signalRGroup && signalR) {
        const signalRUpdate: TocActionSignal = {
          hierarchy: newHierarchy,
          items: [
            ...(response.data.sections
              .filter((i) => changeList.includes(i.id))
              .map((s) => ({
                id: s.id,
                primaryHierarchyId: newHierarchy.id,
                tocNumber: s.sectionNumber,
                tocLabel: s.sectionLabel,
                type: eTlfItemType.section,
                tocOrder: s.order,
                changed: false,
                inError: false,
              })) as MosaicTocSectionDto[]),
            ...(response.data.shells
              .filter((i) => changeList.includes(i.id))
              .map((i) => ({
                ...i,
                changed: false,
                inError: false,
              })) as MosaicTocEntryDto[]),
          ],
          deletedItemId: tocItemsToUpdate.deletedItemId,
        };
        const sentMessage: HubGroupMessage = await signalR.sendTocUpdateToGroup(
          signalRUpdate,
          signalRGroup,
        );
        const newTlfMessage: TocUpdateMessage = {
          messageId: sentMessage.messageId,
          updatedIds: ["sentMessage"],
          updateTS: new Date(sentMessage.timeStamp),
        };
        const newReceivedMessages = [...receivedMessages, newTlfMessage];
        setReceivedMessages(newReceivedMessages);
      }
    } else if (response?.success === false) {
      console.warn("Save failed", response?.errorText);
      dispatch({
        operation: SAVE_FAIL,
      });
    }
  });

  /**
   * Save effect
   */
  useEffect(() => {
    if (state.saving && !saveRequestStatus.requesting && hierarchy) {
      const tocItemsToUpdate: TocItemsUpdateProps = {
        hierarchyId: state.hierarchyId,
        items: state.tocItems
          .filter((i) => i.changed)
          .map((n) => ({
            tocId: n.id,
            tocNumber:
              n.tocNumber ??
              // istanbul ignore next
              "",
            tocLabel:
              n.tocLabel ??
              // istanbul ignore next
              "",
            tlfType: n.type,
            order:
              n.tocOrder ??
              // istanbul ignore next
              0,
          })),
        deletedItemId: state.removedItems?.findLast((i) => i.changed)?.id,
      };
      startTransition(() => {
        saveItems(tocItemsToUpdate);
      });
    }
  }, [
    hierarchy,
    saveRequestStatus.requesting,
    state.hierarchyId,
    state.removedItems,
    state.saving,
    state.tocItems,
  ]);

  /**
   * Send update to get the current hierarchy version
   */
  useEffect(() => {
    if (state.blocked === "getNewVersion" && !saveRequestStatus.requesting && hierarchy) {
      const tocItemsToUpdate: TocItemsUpdateProps = {
        hierarchyId: state.hierarchyId,
        items: [],
      };
      startTransition(() => {
        saveItems(tocItemsToUpdate);
      });
    }
  }, [hierarchy, saveRequestStatus.requesting, state.blocked, state.hierarchyId]);

  /**
   * Receive messages from SignalR
   */
  const [showNeedRefresh, setShowNeedRefresh] = useState<boolean>(false);
  useEffect(() => {
    /**
     * Handle incoming SignalR group messages for ToC updates.
     *
     * @param message - Raw JSON string containing the group message payload.
     */
    const processSignalRMessage = (message: string) => {
      const tocUpdate = JSON.parse(message) as HubGroupMessage;
      const tocItemsToUpdate =
        tocUpdate.text && tocUpdate.text[0].startsWith("{")
          ? (JSON.parse(tocUpdate.text[0]) as TocActionSignal)
          : undefined;
      // Get message info
      if (
        tocUpdate.messageType === "tooBig" &&
        tocUpdate.displayName !== signalR?.connection?.connectionId
      ) {
        setShowNeedRefresh(true);
        return;
      }
      if (
        hierarchy &&
        tocUpdate.displayName !== signalR?.connection?.connectionId &&
        tocUpdate.displayName !== "Backend" &&
        ["tocUpdate", "tocSave"].includes(tocUpdate.messageType) &&
        tocItemsToUpdate &&
        !receivedMessages.find((r) => r.messageId === tocUpdate.messageId)
      ) {
        // Update processing
        const newAction = processActionFromSignal(tocUpdate.messageId, tocItemsToUpdate);
        setReceivedMessages([...receivedMessages, newAction.receivedMessage]);
        /* istanbul ignore else */
        if (
          tocItemsToUpdate.hierarchy.id === hierarchy.id &&
          tocUpdate.messageType === "tocUpdate"
        ) {
          dispatch(newAction);
          dispatch({ operation: SAVE_SUCCESS, saveTimeStamp: Date.now() });
          hierarchyContext.dispatch({
            operation: SAVE_HIERARCHY_COMPLETED,
            hierarchy: tocItemsToUpdate.hierarchy,
          });
        } else if (
          tocItemsToUpdate.hierarchy.id !== hierarchy.id &&
          tocUpdate.messageType === "tocSave"
        ) {
          setShowSaveAlert(true);
          setTocIdFromMessage(tocItemsToUpdate?.hierarchy.id);
          hierarchyContext.dispatch({
            operation: SET_HIERARCHY_ID,
            hierarchyId: tocItemsToUpdate.hierarchy.id,
          });
        }
      }
    };
    // Add listener for SignalR messages
    signalR?.connection?.on("SendMessageToGroup", processSignalRMessage);
    // Remove listener on unmount
    // return () => {
    //   signalR?.connection?.off("SendMessageToGroup", processSignalRMessage);
    // };
  }, [hierarchy, hierarchyContext, receivedMessages, signalR?.connection]);

  const [tocRequest, setTocRequest] = useState<IRequestStatus<HierarchyQueryParams>>({
    requesting: false,
    error: false,
  });

  /**
   * Initial load of ToC items
   */
  useEffect(() => {
    if (hierarchy && hierarchy.level === eHierarchyLevel.Report) {
      // Check if there are zero items loaded
      if (state.tocItems.length === 0 || state.hierarchyId !== hierarchy.id) {
        refreshToc(tocRequest, setTocRequest, appSettings.backEndUrl, hierarchy, dispatch, false);
      }
    }
  }, [appSettings.backEndUrl, hierarchy, state.hierarchyId, state.tocItems.length, tocRequest]);

  const navigation = useNavigation();
  const isNavigating =
    navigation.state === "loading" &&
    (navigation.location.pathname.includes("/Report/") ||
      navigation.location.pathname.includes("/Study/"));
  const isLoading =
    !tocRequest.error &&
    (tocRequest.requesting ||
      isNavigating ||
      (hierarchy?.level === eHierarchyLevel.Report && state.hierarchyId !== hierarchy?.id));

  return hierarchyContext?.hierarchy?.level === eHierarchyLevel.Report ? (
    <TocContext
      value={{
        state,
        dispatch,
        tocRequest: { ...tocRequest, requesting: isLoading },
      }}
    >
      {children}
      <PopupAlertModal
        id="toc-save-alert"
        show={showSaveAlert}
        title="New Major Version Available"
        message="The ToC has been updated and saved as a new version. Please close this Popup to redirect."
        onClose={() => {
          setShowSaveAlert(false);
          navigate(`/R/${tocIdFromMessage}`);
          setTocIdFromMessage(undefined);
        }}
      />
      <PopupAlertModal
        id="toc-need-refresh"
        show={showNeedRefresh}
        title="Large change made"
        message="The ToC has been updated by someone else/in another browser tab, and needs to be refreshed."
        onClose={() => {
          setShowNeedRefresh(false);
          doReload();
        }}
      />
    </TocContext>
  ) : (
    <>{children}</>
  );
};

TocContextProvider.displayName = "TocContextProvider";

export default TocContextProvider;
